import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import appConfig from "../../config/appConfig";
import { confirmDeleteConfig } from "../../config/confirmConfig";
import { ERROR_401 } from "../../config/errorPage";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfig,
  toastErrorMessageWithParameterConfig,
} from "../../config/toastConfig";
import { NUMBER_ELEMENT_PER_PAGE } from "../../constants/dataTable";
import { loadCss } from "../../helpers/function";
import { verifyIfAnyPermission, verifyPermission } from "../../helpers/permission";
import { DeleteConfirmation } from "../components/ConfirmationAlert";
import { ToastBottomEnd } from "../components/Toast";

import EmptyTable from "../components/EmptyTable";
import ExportButton from "../components/ExportButton";
import HeaderTablePage from "../components/HeaderTablePage";
import InfirmationTable from "../components/InfirmationTable";
import LoadingTable from "../components/LoadingTable";
import Pagination from "../components/Pagination";
import UnitTableRow from "../../components/UnitTableRow";
import { usePagination } from "../../hooks/usePagination";
import { useUnits } from "../../hooks/useUnits";
import { createEndpoints } from "../../services/unitService";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const Unit = ({ userPermissions, plan }) => {
  const { t, ready } = useTranslation();

  const hasListPermission = useMemo(() => {
    return (
      verifyPermission(userPermissions, "list-any-unit") ||
      verifyPermission(userPermissions, "list-my-unit") ||
      verifyPermission(userPermissions, "list-without-link-unit")
    );
  }, [userPermissions]);

  const endPoint = useMemo(() => {
    return createEndpoints(plan, userPermissions);
  }, [plan, userPermissions]);

  const {
    filteredUnits,
    loading,
    searchTerm,
    setSearchTerm,
    removeUnit,
  } = useUnits(endPoint);

  const {
    currentPage,
    perPage,
    totalPages,
    currentData: displayedUnits,
    goToPage,
    nextPage,
    prevPage,
    changeItemsPerPage,
  } = usePagination(filteredUnits, NUMBER_ELEMENT_PER_PAGE, filteredUnits.length);

  const handleSearch = useCallback(
    (e) => {
      setSearchTerm(e.target.value);
    },
    [setSearchTerm]
  );

  const handleDeleteUnit = useCallback(
    (unitId) => {
      DeleteConfirmation.fire(confirmDeleteConfig()).then((result) => {
        if (result.value) {
          removeUnit(unitId)
            .then((success) => {
              if (success) {
                ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
              }
            })
            .catch((error) => {
              if (error.response?.data?.error) {
                ToastBottomEnd.fire(
                  toastErrorMessageWithParameterConfig(
                    error.response.data.error
                  )
                );
              } else {
                ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
              }
            });
        }
      });
    },
    [removeUnit]
  );

  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i);
  }, [totalPages]);

  const showInstitution = useMemo(() => {
    return verifyPermission(userPermissions, "list-any-unit");
  }, [userPermissions]);

  useEffect(() => {
    if (!hasListPermission) {
      window.location.href = ERROR_401;
    }
  }, [hasListPermission]);

  if (!hasListPermission || !ready) {
    return null;
  }

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              {/* Fix for the ESLint anchor-is-valid warning */}
              <Link to="/" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </Link>
              <span className="kt-subheader__breadcrumbs-separator" />
              <button
                className="kt-subheader__breadcrumbs-link"
                style={{ cursor: "text", background: "none", border: "none" }}
              >
                {t("Unité")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <InfirmationTable
          information={t("Liste des services, agences, etc") + "..."}
        />

        <div className="kt-portlet">
          <HeaderTablePage
            addPermission={[
              "store-any-unit",
              "store-my-unit",
              "store-without-link-unit",
            ]}
            title={t("Unité")}
            addText={verifyIfAnyPermission(userPermissions, ["store-any-unit", "store-my-unit", "store-without-link-unit"]) && t("Ajouter")}
            addLink={verifyIfAnyPermission(userPermissions, ["store-any-unit", "store-my-unit", "store-without-link-unit"]) && "/settings/unit/add"}
          />

          {loading ? (
            <LoadingTable />
          ) : (
            <div className="kt-portlet__body">
              <div
                id="kt_table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4"
              >
                <div className="row">
                  <div className="col-sm-6 text-left">
                    <div id="kt_table_1_filter" className="dataTables_filter">
                      <label>
                        {t("Recherche")}:
                        <input
                          id="myInput"
                          type="text"
                          defaultValue={searchTerm}
                          onChange={handleSearch}
                          className="form-control form-control-sm"
                          placeholder={t("Nom Unité, Responsable")}
                          aria-controls="kt_table_1"
                        />
                      </label>
                    </div>
                  </div>

                  <ExportButton
                    downloadLink={verifyIfAnyPermission(userPermissions, ["store-any-unit", "store-my-unit", "store-without-link-unit"]) && `${appConfig.apiDomaine}/download-excel/units`}
                    pageUrl={verifyIfAnyPermission(userPermissions, ["store-any-unit", "store-my-unit", "store-without-link-unit"]) && "/settings/unit/import"}
                  />
                </div>

                <div className="row">
                  <div className="col-sm-12">
                    <table
                      className="table table-striped table-bordered table-hover table-checkable dataTable dtr-inline"
                      id="myTable"
                      role="grid"
                      aria-describedby="kt_table_1_info"
                      style={{ width: "952px" }}
                    >
                      <thead>
                        <tr role="row">
                          <th className="sorting">{t("Nom Unité")}</th>
                          <th className="sorting">{t("Type Unité")}</th>
                          <th className="sorting">{t("Unité d'Escalade")}</th>
                          <th className="sorting">{t("Pays")}</th>
                          <th className="sorting">{t("Zones")}</th>
                          <th className="sorting">{t("Responsable")}</th>
                          {showInstitution && (
                            <th className="sorting">{t("Institution")}</th>
                          )}
                          <th className="sorting">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUnits.length > 0 ? (
                          displayedUnits.map((unit) => (
                            <UnitTableRow
                              key={unit.id}
                              unit={unit}
                              onDelete={handleDeleteUnit}
                              showInstitution={showInstitution}
                            />
                          ))
                        ) : (
                          <EmptyTable search={searchTerm.length > 0} />
                        )}
                      </tbody>
                      <tfoot>
                        <tr>
                          <th>{t("Nom Unité")}</th>
                          <th>{t("Type Unité")}</th>
                          <th>{t("Unité d'Escalade")}</th>
                          <th>{t("Pays")}</th>
                          <th>{t("Zones")}</th>
                          <th>{t("Responsable")}</th>
                          {showInstitution && <th>{t("Institution")}</th>}
                          <th>{t("Actions")}</th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                <div className="row">
                  <div className="col-sm-12 col-md-5">
                    <div
                      className="dataTables_info"
                      id="kt_table_1_info"
                      role="status"
                      aria-live="polite"
                    >
                      {t("Affichage de")} {displayedUnits.length} {t("sur")}{" "}
                      {filteredUnits.length} {t("données")}
                    </div>
                  </div>
                  {displayedUnits.length > 0 && (
                    <div className="col-sm-12 col-md-7 dataTables_pager">
                      <Pagination
                        numberPerPage={perPage}
                        onChangeNumberPerPage={(e) =>
                          changeItemsPerPage(parseInt(e.target.value))
                        }
                        activeNumberPage={currentPage}
                        onClickPreviousPage={prevPage}
                        pages={pageNumbers}
                        onClickPage={(e, number) => {
                          e.preventDefault();
                          goToPage(number);
                        }}
                        numberPage={totalPages}
                        onClickNextPage={nextPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    userPermissions: state.user.user.permissions,
    plan: state.plan.plan,
  };
};

export default connect(mapStateToProps)(Unit);
